/**
 * EditorialRecommendations — a numbered list under a small-caps
 * 'Recommendations' head with a single accent LEFT-rule, replacing the
 * old green box.
 *
 * Editorial reference: `.recs` in docs/design/journal-section-layouts.html
 *   border-left: 2px solid var(--accent);  ← roleColor(theme,'accent')
 *   head: mono, .18em tracking, uppercase, accent ink
 *   numbered list (default upper-roman markers in the accent ink)
 *
 * Theme-aware: left-rule, head, and markers all read roleColor(theme,
 * 'accent'); body text reads roleColor(theme,'ink'). No literal green.
 */
import React from 'react';
import type { ReportTheme } from '../../../../types/reportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { reportShellBodyFontSize, REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';
import { useSectionShellContext } from '../../sections/SectionShell';
import { hasGeneratedEditorialLayer } from '../../../../lib/editorial/resolveEditorialLayer';

export interface EditorialRecommendationsProps {
  theme: ReportTheme;
  /** Recommendation lines. Empty/whitespace-only entries are dropped. */
  items: React.ReactNode[];
  /** Head label. Defaults to 'Recommendations'. */
  heading?: React.ReactNode;
  /** Marker style. 'roman' (default) | 'decimal'. */
  marker?: 'roman' | 'decimal';
  id?: string;
  className?: string;
  style?: React.CSSProperties;
}

const MONO = 'var(--report-body-font, inherit)';

function isRenderableEntry(node: React.ReactNode): boolean {
  if (node == null || node === false) return false;
  if (typeof node === 'string') return node.trim().length > 0;
  return true;
}

export function EditorialRecommendations({
  theme,
  items,
  heading = 'Recommendations',
  marker = 'roman',
  id,
  className,
  style,
}: EditorialRecommendationsProps): React.ReactElement | null {
  const { report, sectionId } = useSectionShellContext();
  if (report && sectionId && hasGeneratedEditorialLayer(report, sectionId)) {
    return null;
  }

  const rendered = items.filter(isRenderableEntry);
  if (rendered.length === 0) return null;

  const accent = roleColor(theme, 'accent');

  const renderItem = (item: React.ReactNode, i: number): React.ReactElement => (
    <li
      key={i}
      // House-style keep-whole hook (2026-06-29) — belt-and-suspenders with the
      // inline `breakInside:'avoid'` below: the paged deck also targets this so
      // an action-item row stays atomic across a page seam (PaginatedReportView).
      data-editorial-keep="true"
      data-editorial-recommendation-row="true"
      style={{
        counterIncrement: 'rc',
        // PAGEDJS-ROBUST — FLEX, not grid. pagedjs collapses inline
        // `display:grid` <li> rows during fragmentation/clone (every
        // row painted at the same y → the overlapping "RECOMMENDATIONS
        // I/II/III" the user reported in the deck + PDF). Flexbox
        // survives the clone, matching the conditions/next-actions
        // flex rows that render cleanly. `breakInside:avoid` keeps each
        // item atomic so it never splits across a page boundary either.
        display: 'flex',
        gap: 10,
        padding: '6px 0',
        breakInside: 'avoid',
        // 14px unified report BODY size (text-sm).
        fontSize: reportShellBodyFontSize(theme),
        lineHeight: 1.55,
        color: roleColor(theme, 'ink'),
        alignItems: 'baseline',
      }}
    >
      <span
        aria-hidden
        style={{ fontFamily: MONO, fontSize: REPORT_TEXT_PX.label, fontWeight: 600, color: accent, flex: '0 0 24px', textAlign: 'left' }}
      >
        {marker === 'roman' ? toRoman(i + 1) : `${i + 1}.`}
      </span>
      <span style={{ flex: '1 1 auto', minWidth: 0 }}>{item}</span>
    </li>
  );

  const listStyle: React.CSSProperties = { listStyle: 'none', margin: 0, padding: 0, counterReset: 'rc' };
  const [first, ...restItems] = rendered;

  return (
    <div
      id={id}
      className={className}
      // William QA 2026-06-28 — left accent rule removed report-wide
      style={{
        padding: '2px 0',
        ...style,
      }}
    >
      {/* P-GROUP Phase A (`head-body`) — bind the small-caps RECOMMENDATIONS
          head to the FIRST item so the head can never strand alone at a page
          foot (the live p4 failure: pagedjs broke between this head div and the
          list). The first item lives in its OWN <ol> inside the atom; the
          remaining items continue in a second <ol> that fragments freely
          (markers are manual spans — toRoman(i+1) — so numbering is unaffected
          by the split; both lists are visually seamless: identical margins,
          padding and row rhythm). */}
      <div data-layout-group="head-body">
        <div
          style={{
            fontFamily: MONO,
            // 12px section-label token (text-xs) — unified across editorial heads.
            fontSize: REPORT_TEXT_PX.label,
            fontWeight: 600,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: accent,
            marginBottom: 10,
          }}
        >
          {heading}
        </div>
        <ol style={listStyle}>{renderItem(first, 0)}</ol>
      </div>
      {restItems.length > 0 && (
        <ol style={listStyle}>
          {restItems.map((item, i) => renderItem(item, i + 1))}
        </ol>
      )}
    </div>
  );
}

/** Upper-roman numeral for small list indices (1..~3999). */
function toRoman(n: number): string {
  if (n <= 0) return String(n);
  const table: Array<[number, string]> = [
    [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'],
    [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'],
    [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
  ];
  let out = '';
  let rem = n;
  for (const [v, s] of table) {
    while (rem >= v) {
      out += s;
      rem -= v;
    }
  }
  return out;
}
